import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './VocabChatView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate, popScale } from './juice'
import { romanizationOf } from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SpellcheckIcon from '@mui/icons-material/Spellcheck'

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition

// Practice the non-English side of each card, matching the other modes.
const practiceSideOf = (card, practiceLang) => {
  const t = card.translations?.[0]
  // When the caller names the language being practised, use that side — the
  // "not English" rule below is wrong for someone learning English.
  if (practiceLang) {
    if (card.sourceLanguage === practiceLang && card.source) return { text: card.source, lang: practiceLang, translation: t?.target || '' }
    if (t?.targetLanguage === practiceLang && t?.target) return { text: t.target, lang: practiceLang, translation: card.source || '' }
  }
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { text: card.source, lang: card.sourceLanguage, translation: t?.target || '' }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { text: t.target, lang: t.targetLanguage, translation: card.source || '' }
  }
  return { text: card.source, lang: card.sourceLanguage || 'eng', translation: t?.target || '' }
}

const PUNCT_RE = /[.,!?;:"'“”‘’¿¡()«»、。，！？：；·…]/g
const normalize = (s) => (s || '').replace(PUNCT_RE, '').replace(/\s+/g, ' ').trim().toLowerCase()
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// ── Spoken-practice matching (same approach as the Flow view) ────────────────
const NO_SPACE_LANGS = new Set(['cmn', 'jpn'])

const tokenizePhrase = (text, lang) => {
  if (!text) return []
  if (NO_SPACE_LANGS.has(lang)) {
    return Array.from(text.replace(/\s+/g, '').replace(PUNCT_RE, '')).map(ch => ({ raw: ch, norm: ch }))
  }
  return text.split(/\s+/).filter(Boolean).map(w => ({
    raw: w,
    norm: w.replace(PUNCT_RE, '').toLowerCase(),
  }))
}

const tokenizeSpoken = (transcript, lang) => {
  if (NO_SPACE_LANGS.has(lang)) {
    return Array.from(transcript.replace(/\s+/g, '').replace(PUNCT_RE, ''))
  }
  return transcript.split(/\s+/).map(w => w.replace(PUNCT_RE, '').toLowerCase()).filter(Boolean)
}

const editDistance = (a, b) => {
  if (a === b) return 0
  const m = a.length, n = b.length
  if (Math.abs(m - n) > 2) return 99
  const row = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = row[j]
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return row[n]
}

const tokenMatches = (spoken, expected) => {
  if (!expected) return false
  if (spoken === expected) return true
  if (expected.length >= 4 && editDistance(spoken, expected) <= 1) return true
  return false
}

// Speech synthesis and speech recognition fight over the audio session
// (especially on iPadOS/Safari): starting the mic while TTS is talking makes
// the recognition session end almost immediately. Always silence TTS first.
const cancelTts = () => {
  try { window.speechSynthesis?.cancel() } catch { /* fine */ }
}

// Small/mid models (qwen especially) sometimes leak their chain-of-thought —
// usually in Chinese — before the actual reply. For non-Han target languages,
// keep only the sentence chunks written in the target's own script and drop
// the reasoning. Belt-and-braces alongside the prompt/params.
const HANGUL_RE = /[가-힣ᄀ-ᇿ㄰-㆏]/
const HAN_RE = /[一-鿿]/
const sanitizeReply = (raw, lang) => {
  const text = (raw || '').trim()
  if (!text) return text
  // Chinese/Japanese legitimately use Han glyphs — can't safely strip there.
  if (NO_SPACE_LANGS.has(lang)) return text
  const chunks = text.match(/[^\n。！？.!?]+[。！？.!?]?/g) || [text]
  const kept = lang === 'kor'
    ? chunks.filter(c => HANGUL_RE.test(c) && !HAN_RE.test(c)) // Korean sentences only
    : chunks.filter(c => !HAN_RE.test(c))                      // drop Han reasoning
  if (kept.length) return kept.join(' ').replace(/\s+/g, ' ').trim()
  // Last resort: strip Han runs from the whole thing.
  const stripped = text.replace(/[一-鿿]+/g, '').replace(/\s+/g, ' ').trim()
  return stripped || text
}

// Romanization comes from the shared helper (Korean only, and never for a
// language the learner speaks natively).

const MODELS = ['gemma4:26b', 'gemma3:27b']

export default function VocabChatView({ cards, voiceSpeed, onClose, practiceLang }) {
  // Dedup vocab by normalized text so repeated cards don't produce twin chips.
  const vocab = useMemo(() => {
    const seen = new Set()
    const out = []
    for (const item of (cards || []).map(c => practiceSideOf(c, practiceLang))) {
      const key = normalize(item.text)
      if (!item.text || !key || seen.has(key)) continue
      seen.add(key)
      out.push({ ...item, norm: key })
    }
    return out
  }, [cards, practiceLang])

  const lang = vocab[0]?.lang || 'eng'
  const langName = languages[lang] || lang

  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  // The target-word list is long (dozens of chips) and buries the chat on
  // mobile, so it's collapsed by default behind a toggle.
  const [chipsOpen, setChipsOpen] = useState(false)
  // One-line English roleplay scene (invented at start) that makes the target
  // words natural — "you're at a café and the owner hands you your order".
  const [scene, setScene] = useState(null)
  const [loadingChat, setLoadingChat] = useState(false)
  const [suggestions, setSuggestions] = useState([]) // [{text, meaning}]
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  // Suggestion picked for spoken practice, with per-word hit states.
  const [practice, setPractice] = useState(null) // {text, meaning}
  const [practiceStates, setPracticeStates] = useState([])
  const [practiceListening, setPracticeListening] = useState(false)
  const [autoSpeak, setAutoSpeak] = useState(true)
  const [showRoman, setShowRoman] = useState(true)
  const [usedWords, setUsedWords] = useState(new Set()) // vocab indices the USER has used
  const [isRecording, setIsRecording] = useState(false)
  const [selectedModel, setSelectedModel] = useState(MODELS[0])
  const [errorMsg, setErrorMsg] = useState(null)
  // Peek-at-English state: deliberately transient so the learner stays in the
  // target language. Message peeks show only while the 👁 button is held;
  // word peeks fade out after a moment.
  // Grammar/quality checks of the learner's OWN messages, keyed by message idx.
  // { loading } | { ok, corrected, note } | { error }
  const [checks, setChecks] = useState({})
  const [checkingAll, setCheckingAll] = useState(false)
  const [msgPeek, setMsgPeek] = useState(null)   // {idx, text|null}
  const [wordPeek, setWordPeek] = useState(null) // {msgIdx, partIdx, text|null}

  const chatLogRef = useRef(null)
  const inputRef = useRef(null)
  const chatControllerRef = useRef(null)
  const suggestControllerRef = useRef(null)
  const recognitionRef = useRef(null)
  const practiceRecRef = useRef(null)
  const practiceStripRef = useRef(null)
  const practiceDoneRef = useRef(false)
  const practiceMicOnRef = useRef(false) // user intent: keep the practice mic alive
  const chipRefs = useRef({})
  const allDoneRef = useRef(false)
  const msgTranslationCacheRef = useRef(new Map())  // message idx → English
  const wordTranslationCacheRef = useRef(new Map()) // clean word → English
  const wordPeekTimerRef = useRef(null)
  const autoSpeakRef = useRef(autoSpeak)
  useEffect(() => { autoSpeakRef.current = autoSpeak }, [autoSpeak])

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  const systemPrompt = useMemo(() => `You are roleplaying as a real person talking with a beginner ${langName} learner.${scene ? ` Scene: ${scene}.` : ''}

The learner is practicing these ${langName} expressions: ${vocab.map(v => `${v.text} (${v.translation || '?'})`).join(', ')}.

Every turn:
- Say ONE short, very simple ${langName} sentence — 8 words maximum, everyday spoken language a beginner can follow.
- Set up a natural moment so the learner can reply with one of their expressions (thank them, apologise, hand them something).
- Do not force one of the target expressions into your own sentence.

Output ONLY your single spoken ${langName} sentence. Do NOT think out loud, do NOT explain, do NOT translate, and do NOT write in any language other than ${langName}.`, [vocab, langName, scene])

  // Highlight regex for target words inside bot messages — longest first so
  // e.g. 괜찮습니다 wins over 괜찮아요 when both could match.
  const highlightRe = useMemo(() => {
    const terms = vocab
      .map(v => v.text.replace(PUNCT_RE, '').trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map(escapeRegex)
    return terms.length ? new RegExp(`(${terms.join('|')})`, 'g') : null
  }, [vocab])

  const callChat = useCallback(async (apiMessages) => {
    if (chatControllerRef.current) chatControllerRef.current.abort()
    chatControllerRef.current = new AbortController()
    const res = await fetch(`${serverUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
      signal: chatControllerRef.current.signal,
      body: JSON.stringify({
        model: selectedModel,
        messages: apiMessages,
        stream: false, keep_alive: '30m',
        // Cap output so it can't ramble into reasoning; one short line is ~40 tokens.
        options: { temperature: 0.6, num_predict: 90 },
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    return data.message?.content?.trim() || ''
  }, [serverUrl, apiKey, selectedModel])

  // Check one of the learner's messages: is it correct/natural, and how could it
  // be said a little better? The "corrected" line is the flow-view equivalent of
  // a pronunciation nudge — a slightly better phrasing to aim for next time.
  const checkMessage = useCallback(async (idx, text) => {
    setChecks(prev => ({ ...prev, [idx]: { loading: true } }))
    if (!serverUrl) { setChecks(prev => ({ ...prev, [idx]: { error: true } })); return }
    const prompt = `A native English speaker is learning ${langName}. They wrote this sentence: "${text}".
Judge whether it is grammatically correct and natural ${langName}.
Respond ONLY with JSON, no other text:
{"ok": true or false,
 "corrected": "the most natural correct version in ${langName} — repeat it unchanged if it is already correct",
 "note": "1-2 short sentences of feedback, written IN ENGLISH: if there is a mistake, explain clearly what was wrong and why; if it is already correct, give a small tip to sound more natural, or brief praise"}

CRITICAL:
- "corrected" is written in ${langName} (its own script).
- "note" MUST be written in ENGLISH — the learner is an English speaker. You may quote a ${langName} word or phrase inside the English explanation, but never write the whole note in ${langName}. Do NOT reply to the note in ${langName}.`
    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ model: selectedModel, prompt, stream: false, keep_alive: '30m', format: 'json', options: { temperature: 0.2, num_predict: 240 } }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const parsed = JSON.parse((data.response || '').replace(/```json|```/g, '').trim())
      let note = (parsed.note || '').trim()
      // The note must be English. If the model returned it entirely in the
      // target script (no Latin letters at all), swap in an English fallback.
      if (note && !/[A-Za-z]/.test(note)) {
        note = parsed.ok
          ? 'Looks correct — nicely done.'
          : 'There’s a small mistake — see the corrected version above.'
      }
      setChecks(prev => ({ ...prev, [idx]: {
        ok: !!parsed.ok,
        corrected: (parsed.corrected || '').trim(),
        note,
      } }))
    } catch (e) {
      setChecks(prev => ({ ...prev, [idx]: { error: true } }))
    }
  }, [serverUrl, apiKey, selectedModel, langName])

  // Check every learner message that hasn't been checked yet, in order.
  const checkAll = useCallback(async () => {
    setCheckingAll(true)
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].role === 'user') {
        // eslint-disable-next-line no-await-in-loop
        await checkMessage(i, messages[i].content)
      }
    }
    setCheckingAll(false)
  }, [messages, checkMessage])

  // Invent a mini roleplay scene where the target words are natural, plus the
  // partner's opening line. Opening with a response-word ("Hello. Thank you.")
  // is nonsense — the opener must CREATE the moment the learner responds to.
  const fetchGreeting = useCallback(async () => {
    setLoadingChat(true)
    setErrorMsg(null)
    if (chatControllerRef.current) chatControllerRef.current.abort()
    chatControllerRef.current = new AbortController()
    const prompt = `You are creating a tiny roleplay to help a beginner practice ${langName}.

Target expressions the learner is practicing:
${vocab.map(v => `- ${v.text} = ${v.translation || '(no translation)'}`).join('\n')}

Invent ONE simple everyday scenario in which these expressions would naturally come up (for example: a café order being handed over, returning something the learner dropped, accidentally bumping into them, giving a small gift). Then write the roleplay partner's opening line in ${langName}: natural for the scenario, very simple (maximum 2 short sentences), and creating a moment the learner would naturally respond to with one of the target expressions. The opening line must NOT itself use the target expressions — it sets up the situation instead.

Respond ONLY with valid JSON in exactly this format, nothing else:
{"scene": "one-sentence English description of the scenario and the learner's role", "opening": "the opening line in ${langName}"}`
    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        signal: chatControllerRef.current.signal,
        body: JSON.stringify({
          model: selectedModel,
          prompt,
          stream: false, keep_alive: '30m',
          format: 'json',
          options: { temperature: 0.9, num_predict: 220 },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const raw = (data.response || '').replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(raw)
      const opening = sanitizeReply(parsed.opening || '', lang)
      if (!opening) throw new Error('empty opening line')
      setScene((parsed.scene || '').trim() || null)
      setMessages([{ role: 'assistant', content: opening }])
      if (autoSpeakRef.current) speak(opening, voices[lang] || voices['eng'], voiceSpeed)
    } catch (err) {
      if (err.name !== 'AbortError') setErrorMsg(`Scene setup failed: ${err.message}`)
    }
    setLoadingChat(false)
  }, [vocab, langName, lang, voiceSpeed, serverUrl, apiKey, selectedModel])

  useEffect(() => {
    if (vocab.length > 0) fetchGreeting()
    return () => {
      chatControllerRef.current?.abort()
      suggestControllerRef.current?.abort()
      practiceMicOnRef.current = false
      try { practiceRecRef.current?.abort() } catch { /* fine */ }
      clearTimeout(wordPeekTimerRef.current)
      cancelTts()
    }
  }, []) // eslint-disable-line

  // Smoothly glide the chat to the newest message so you're reading the reply
  // you're about to respond to.
  useEffect(() => {
    const el = chatLogRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages, loadingChat])

  // When the reply suggestions arrive they take up room below the chat; nudge
  // the scroll again so the latest reply stays in view as the options appear.
  useEffect(() => {
    if (suggestions.length === 0) return
    const el = chatLogRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [suggestions])

  // Which target words did this user message use? Mark chips + celebrate.
  const trackUsage = useCallback((userText) => {
    const norm = normalize(userText)
    if (!norm) return
    setUsedWords(prev => {
      const next = new Set(prev)
      vocab.forEach((v, i) => {
        if (!next.has(i) && norm.includes(v.norm)) {
          next.add(i)
          const el = chipRefs.current[i]
          if (el) {
            const rect = el.getBoundingClientRect()
            celebrate(rect.left + rect.width / 2, rect.top + rect.height / 2, { count: 14, spread: 80 })
            popScale(el)
          }
        }
      })
      if (next.size === vocab.length && vocab.length > 0 && !allDoneRef.current) {
        allDoneRef.current = true
        setTimeout(() => celebrate(window.innerWidth / 2, window.innerHeight / 3, { count: 50, spread: 260 }), 300)
      }
      return next
    })
  }, [vocab])

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim()
    if (!trimmed || loadingChat) return
    trackUsage(trimmed)
    const history = [...messages, { role: 'user', content: trimmed }]
    setMessages(history)
    setInput('')
    setSuggestions([])
    suggestControllerRef.current?.abort()
    setPractice(null)
    practiceMicOnRef.current = false
    try { practiceRecRef.current?.abort() } catch { /* fine */ }
    setLoadingChat(true)
    setErrorMsg(null)
    try {
      const reply = sanitizeReply(await callChat([{ role: 'system', content: systemPrompt }, ...history]), lang)
      setMessages(prev => [...prev, { role: 'assistant', content: reply }])
      if (autoSpeakRef.current && reply) speak(reply, voices[lang] || voices['eng'], voiceSpeed)
    } catch (err) {
      if (err.name !== 'AbortError') setErrorMsg(`Reply failed: ${err.message}`)
    }
    setLoadingChat(false)
  }, [messages, loadingChat, trackUsage, callChat, systemPrompt, lang, voiceSpeed])

  // ── Reply suggestions ───────────────────────────────────────────────────────
  // After every partner message, offer 3 ready-made replies the learner can
  // study (romanization + meaning) and practice out loud before sending.
  const fetchSuggestions = useCallback(async (currentMessages) => {
    if (suggestControllerRef.current) suggestControllerRef.current.abort()
    suggestControllerRef.current = new AbortController()
    setLoadingSuggestions(true)
    const unused = vocab.filter((_, i) => !usedWords.has(i))
    const focus = (unused.length ? unused : vocab).slice(0, 8)
    const recent = currentMessages.slice(-6) // recent turns matter most
    const contextStr = recent.map(m => `${m.role === 'assistant' ? 'Partner' : 'Me'}: ${m.content}`).join('\n')
    const lastPartner = [...currentMessages].reverse().find(m => m.role === 'assistant')?.content || ''
    const prompt = `You are helping a beginner learning ${langName} decide what to say NEXT in a conversation.
${scene ? `Roleplay scenario: ${scene}\n` : ''}Recent conversation:
${contextStr}

The partner JUST said: "${lastPartner}"

Suggest 3 short, simple replies (2-8 words each) the learner could send right now.

MOST IMPORTANT RULE: every reply must directly and naturally respond to what the partner just said. If they asked a question, the reply must ANSWER that question. If they made a statement, react to it. Do not change the subject or reply with something unrelated.

Only when it genuinely fits the reply, you may weave in one of these words the learner is practicing: ${focus.map(v => `${v.text} (${v.translation || '?'})`).join(', ')}. Relevance to the partner's message ALWAYS comes first — never force a target word into a reply that doesn't answer them.

For each reply give a short English meaning.

Respond ONLY with valid JSON in exactly this format, nothing else:
{"suggestions": [{"text": "reply in ${langName}", "meaning": "English meaning"}, {"text": "...", "meaning": "..."}, {"text": "...", "meaning": "..."}]}`
    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        signal: suggestControllerRef.current.signal,
        body: JSON.stringify({
          model: selectedModel,
          prompt,
          stream: false, keep_alive: '30m',
          format: 'json',
          options: { temperature: 0.8, num_predict: 250 },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const raw = (data.response || '').replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(raw)
      const clean = (parsed.suggestions || [])
        .filter(s => s && typeof s.text === 'string' && s.text.trim())
        .map(s => ({ text: s.text.trim(), meaning: (s.meaning || '').trim() }))
        .slice(0, 3)
      setSuggestions(clean)
    } catch (err) {
      if (err.name !== 'AbortError') setSuggestions([])
    }
    setLoadingSuggestions(false)
  }, [vocab, usedWords, serverUrl, apiKey, selectedModel, langName, scene])

  // Auto-fetch suggestions whenever the partner speaks (incl. the greeting).
  useEffect(() => {
    const last = messages[messages.length - 1]
    if (last?.role === 'assistant') fetchSuggestions(messages)
  }, [messages]) // eslint-disable-line

  // ── Spoken practice of a picked suggestion ──────────────────────────────────
  const practiceWords = useMemo(
    () => (practice ? tokenizePhrase(practice.text, lang) : []),
    [practice, lang]
  )

  const selectSuggestion = (s) => {
    practiceMicOnRef.current = false
    try { practiceRecRef.current?.abort() } catch { /* fine */ }
    practiceDoneRef.current = false
    setPractice(s)
    setPracticeStates(Array(tokenizePhrase(s.text, lang).length).fill('pending'))
    setPracticeListening(false)
    speak(s.text, voices[lang] || voices['eng'], voiceSpeed)
  }

  const closePractice = () => {
    practiceMicOnRef.current = false
    try { practiceRecRef.current?.abort() } catch { /* fine */ }
    setPractice(null)
    setPracticeListening(false)
  }

  const applyPracticeTranscript = useCallback((transcript) => {
    const tokens = tokenizeSpoken(transcript, lang)
    setPracticeStates(prev => {
      const next = [...prev]
      let wi = 0
      for (const tok of tokens) {
        for (let j = wi; j < Math.min(next.length, wi + 3); j++) {
          if (next[j] === 'hit') { if (j === wi) wi++; continue }
          if (tokenMatches(tok, practiceWords[j]?.norm, lang)) {
            next[j] = 'hit'
            wi = j + 1
            break
          }
        }
      }
      return next
    })
  }, [lang, practiceWords])

  const togglePracticeMic = () => {
    if (!SpeechRecognitionAPI) {
      setErrorMsg('Speech recognition is not supported in this browser.')
      return
    }
    if (practiceListening) {
      practiceMicOnRef.current = false
      try { practiceRecRef.current?.stop() } catch { /* fine */ }
      return
    }
    cancelTts()
    setErrorMsg(null)
    practiceMicOnRef.current = true
    const rec = new SpeechRecognitionAPI()
    rec.lang = recognitionLangs[lang] || 'en-US'
    rec.interimResults = true
    rec.continuous = true
    rec.onstart = () => setPracticeListening(true)
    // Keep the mic alive across silence / premature session drops until the
    // user taps stop or the phrase completes.
    rec.onend = () => {
      if (practiceMicOnRef.current && !practiceDoneRef.current) {
        try { rec.start() } catch { setPracticeListening(false) }
      } else {
        setPracticeListening(false)
      }
    }
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        practiceMicOnRef.current = false
        setErrorMsg('Microphone access was blocked — allow it in browser settings.')
      } else if (e.error === 'audio-capture') {
        practiceMicOnRef.current = false
        setErrorMsg('No microphone available for audio capture.')
      }
    }
    rec.onresult = (event) => {
      let transcript = ''
      for (let i = 0; i < event.results.length; i++) transcript += event.results[i][0].transcript + ' '
      applyPracticeTranscript(transcript)
    }
    practiceRecRef.current = rec
    try { rec.start() } catch { /* fine */ }
  }

  // Whole phrase spoken correctly → celebrate and send it into the chat.
  useEffect(() => {
    if (!practice || practiceWords.length === 0) return
    if (practiceStates.length !== practiceWords.length) return
    if (!practiceStates.every(s => s === 'hit') || practiceDoneRef.current) return
    practiceDoneRef.current = true
    practiceMicOnRef.current = false
    try { practiceRecRef.current?.abort() } catch { /* fine */ }
    const el = practiceStripRef.current
    if (el) {
      const rect = el.getBoundingClientRect()
      celebrate(rect.left + rect.width / 2, rect.top + rect.height / 2, { count: 26, spread: 150 })
    }
    const text = practice.text
    setTimeout(() => sendMessage(text), 850)
  }, [practiceStates, practice, practiceWords, sendMessage])

  // ── Dictation (single utterance appended to the input) ─────────────────────
  const toggleRecord = () => {
    if (!SpeechRecognitionAPI) {
      setErrorMsg('Speech recognition is not supported in this browser.')
      return
    }
    if (isRecording) {
      recognitionRef.current?.stop()
      return
    }
    cancelTts()
    setErrorMsg(null)
    const rec = new SpeechRecognitionAPI()
    rec.lang = recognitionLangs[lang] || 'en-US'
    rec.interimResults = false
    rec.onstart = () => setIsRecording(true)
    rec.onend = () => setIsRecording(false)
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setErrorMsg('Microphone access was blocked — allow it in browser settings.')
      } else if (e.error === 'audio-capture') {
        setErrorMsg('No microphone available for audio capture.')
      }
    }
    rec.onresult = (event) => {
      const transcript = event.results[0][0].transcript
      setInput(prev => (prev.trim() ? prev.trim() + ' ' : '') + transcript)
    }
    recognitionRef.current = rec
    try { rec.start() } catch { /* fine */ }
  }

  const speakWord = (text) => speak(text, voices[lang] || voices['eng'], voiceSpeed)

  // ── Peek at English ─────────────────────────────────────────────────────────

  // Whole-message peek: visible only while the eye button is held down.
  const peekMessage = useCallback(async (idx, content) => {
    const cached = msgTranslationCacheRef.current.get(idx)
    setMsgPeek({ idx, text: cached ?? null })
    if (cached != null) return
    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({
          model: selectedModel,
          prompt: `Translate this ${langName} message into natural English: "${content}". Respond with ONLY the English translation, nothing else.`,
          stream: false, keep_alive: '30m',
          options: { temperature: 0.1, num_predict: 80 },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const text = (data.response || '').trim().replace(/^["'`]+|["'`]+$/g, '') || '(no translation)'
      msgTranslationCacheRef.current.set(idx, text)
      setMsgPeek(prev => (prev && prev.idx === idx ? { idx, text } : prev))
    } catch {
      setMsgPeek(prev => (prev && prev.idx === idx ? { idx, text: '(translation failed)' } : prev))
    }
  }, [serverUrl, apiKey, selectedModel, langName])

  const endMsgPeek = () => setMsgPeek(null)

  // Single-word peek: tooltip above the tapped word, fades after a moment.
  const peekWord = useCallback(async (msgIdx, partIdx, word, fullMessage) => {
    const clean = word.replace(PUNCT_RE, '').trim()
    if (!clean) return
    clearTimeout(wordPeekTimerRef.current)
    const scheduleClear = () => {
      wordPeekTimerRef.current = setTimeout(() => setWordPeek(null), 2800)
    }
    // Target vocab: we already know the meaning — no round-trip needed.
    const target = vocab.find(v => v.norm === normalize(word))
    const cached = target?.translation || wordTranslationCacheRef.current.get(clean)
    if (cached) {
      setWordPeek({ msgIdx, partIdx, text: cached })
      scheduleClear()
      return
    }
    setWordPeek({ msgIdx, partIdx, text: null })
    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({
          model: selectedModel,
          prompt: `Translate the word "${clean}" from ${langName} to English based on its context within the sentence: "${fullMessage}". Respond with ONLY the short English translation of that specific word. No quotes, no extra text.`,
          stream: false, keep_alive: '30m',
          options: { temperature: 0.1, num_predict: 12 },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const text = (data.response || '').trim().replace(/^["'`]+|["'`]+$/g, '') || '?'
      wordTranslationCacheRef.current.set(clean, text)
      setWordPeek(prev => {
        if (prev && prev.msgIdx === msgIdx && prev.partIdx === partIdx) {
          scheduleClear()
          return { msgIdx, partIdx, text }
        }
        return prev
      })
    } catch {
      setWordPeek(prev => {
        if (prev && prev.msgIdx === msgIdx && prev.partIdx === partIdx) {
          scheduleClear()
          return { msgIdx, partIdx, text: '?' }
        }
        return prev
      })
    }
  }, [vocab, serverUrl, apiKey, selectedModel, langName])

  // Render a bot message with target words highlighted; tapping any word
  // speaks it and briefly peeks its English meaning.
  const renderAssistantContent = (content, msgIdx) => {
    const parts = highlightRe ? content.split(highlightRe) : [content]
    const renderWord = (word, partIdx, className) => {
      const isPeeked = wordPeek && wordPeek.msgIdx === msgIdx && wordPeek.partIdx === partIdx
      return (
        <span
          key={partIdx}
          className={className}
          onClick={() => { speakWord(word); peekWord(msgIdx, partIdx, word, content) }}
        >
          {word}
          {isPeeked && (
            <span className={styles.wordTooltip}>{wordPeek.text ?? '…'}</span>
          )}
        </span>
      )
    }
    return parts.map((part, i) => {
      if (!part) return null
      if (highlightRe && i % 2 === 1) {
        return renderWord(part, `t-${i}`, styles.targetWord)
      }
      // Plain chunks may hold several words — peek one word at a time.
      return part.split(/(\s+)/).map((w, j) =>
        /^\s*$/.test(w) ? w : renderWord(w, `${i}-${j}`, styles.plainWord)
      )
    })
  }

  const restart = () => {
    setMessages([])
    setUsedWords(new Set())
    allDoneRef.current = false
    setInput('')
    setScene(null)
    setSuggestions([])
    setChecks({})
    closePractice()
    fetchGreeting()
  }

  const userMsgCount = messages.filter(m => m.role === 'user').length

  if (vocab.length === 0) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.empty}>No vocabulary to chat with.</div>
      </div>
    )
  }

  const allDone = usedWords.size === vocab.length

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>

      <div className={styles.header}>
        <div>
          <div className={styles.modeLabel}>Vocab Chat · {langName}</div>
          <div className={styles.subLabel}>
            {allDone
              ? 'All words used — amazing! Keep chatting or restart with a fresh conversation.'
              : `Use every target word in conversation — ${usedWords.size} / ${vocab.length} so far`}
          </div>
        </div>
        <div className={styles.headerBtns}>
          {userMsgCount > 0 && (
            <button
              className={styles.chipsToggle}
              onClick={checkAll}
              disabled={checkingAll}
              title="Check every sentence you've written for grammar & naturalness"
            >
              <SpellcheckIcon style={{ fontSize: 14, verticalAlign: '-2px' }} /> {checkingAll ? 'Checking…' : 'Check all'}
            </button>
          )}
          <button
            className={`${styles.chipsToggle} ${chipsOpen ? styles.chipsToggleOpen : ''}`}
            onClick={() => setChipsOpen(o => !o)}
            title={chipsOpen ? 'Hide the word list' : 'Show the word list'}
          >
            {chipsOpen ? '▾' : '▸'} Words {usedWords.size}/{vocab.length}
          </button>
          <button className={styles.restartBtn} onClick={restart} title="Restart conversation">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {chipsOpen && (
        <div className={styles.chipBar}>
          {vocab.map((v, i) => (
            <button
              key={i}
              ref={el => { chipRefs.current[i] = el }}
              className={`${styles.wordChip} ${usedWords.has(i) ? styles.wordChipUsed : ''}`}
              onClick={() => speakWord(v.text)}
              title={v.translation || undefined}
            >
              {v.text}
            </button>
          ))}
        </div>
      )}

      {scene && (
        <div className={styles.sceneBanner}>
          <span className={styles.sceneIcon}>🎬</span>
          <span>{scene}</span>
        </div>
      )}

      <div className={styles.chatLog} ref={chatLogRef}>
        {messages.map((m, idx) => (
          <div key={idx} className={`${styles.messageWrap} ${m.role === 'user' ? styles.user : styles.assistant}`}>
            <div className={styles.bubble}>
              {m.role === 'assistant' ? renderAssistantContent(m.content, idx) : m.content}
              {m.role === 'assistant' && showRoman && romanizationOf(m.content, lang) && (
                <div className={styles.bubbleRoman}>{romanizationOf(m.content, lang)}</div>
              )}
              {m.role === 'assistant' && msgPeek?.idx === idx && (
                <div className={styles.peekLine}>{msgPeek.text ?? '…'}</div>
              )}
            </div>
            <div className={styles.bubbleActions}>
              <button className={styles.bubbleSpeakBtn} onClick={() => speakWord(m.content)} title="Hear it">
                <VolumeUpIcon style={{ fontSize: 16 }} />
              </button>
              {m.role === 'assistant' && (
                <button
                  className={styles.bubbleSpeakBtn}
                  onPointerDown={() => peekMessage(idx, m.content)}
                  onPointerUp={endMsgPeek}
                  onPointerLeave={endMsgPeek}
                  onPointerCancel={endMsgPeek}
                  onContextMenu={(e) => e.preventDefault()}
                  title="Hold to peek at the English"
                >
                  <VisibilityIcon style={{ fontSize: 16 }} />
                </button>
              )}
              {m.role === 'user' && (
                <button
                  className={styles.bubbleSpeakBtn}
                  onClick={() => checkMessage(idx, m.content)}
                  disabled={checks[idx]?.loading}
                  title="Check this sentence"
                >
                  <SpellcheckIcon style={{ fontSize: 16 }} />
                </button>
              )}
            </div>

            {m.role === 'user' && checks[idx] && (
              <div className={`${styles.checkPanel} ${checks[idx].error ? '' : checks[idx].ok ? styles.checkOk : styles.checkWarn}`}>
                {checks[idx].loading ? (
                  <span className={styles.typing}><span /><span /><span /></span>
                ) : checks[idx].error ? (
                  <span className={styles.checkNote}>Couldn't check that — is the Mac Studio reachable?</span>
                ) : (
                  <>
                    <div className={styles.checkHead}>{checks[idx].ok ? '✓ Looks good' : '✎ Could be better'}</div>
                    {checks[idx].corrected && normalize(checks[idx].corrected) !== normalize(m.content) && (
                      <div className={styles.checkCorrected}>
                        <span>{checks[idx].corrected}</span>
                        <button className={styles.checkSpeak} onClick={() => speakWord(checks[idx].corrected)} title="Hear the better version">
                          <VolumeUpIcon style={{ fontSize: 14 }} />
                        </button>
                      </div>
                    )}
                    {checks[idx].note && <div className={styles.checkNote}>{checks[idx].note}</div>}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        {loadingChat && (
          <div className={`${styles.messageWrap} ${styles.assistant}`}>
            <div className={styles.bubble}>
              <span className={styles.typing}><span /><span /><span /></span>
            </div>
          </div>
        )}
        {errorMsg && <div className={styles.error}>{errorMsg}</div>}
      </div>

      {practice && (
        <div className={styles.practiceStrip} ref={practiceStripRef}>
          <div className={styles.practiceLabel}>
            {practiceStates.every(s => s === 'hit') && practiceStates.length > 0
              ? 'Perfect — sending it! 🎉'
              : 'Practice out loud — say it right and it sends itself'}
          </div>
          <div className={styles.practiceWords}>
            {practiceWords.map((w, i) => (
              <span
                key={i}
                className={[
                  styles.practiceWord,
                  practiceStates[i] === 'hit' ? styles.practiceWordHit : '',
                ].join(' ')}
                onClick={() => speakWord(w.raw)}
              >
                {w.raw}
              </span>
            ))}
          </div>
          {romanizationOf(practice.text, lang) && (
            <div className={styles.practiceRoman}>{romanizationOf(practice.text, lang)}</div>
          )}
          {practice.meaning && <div className={styles.practiceMeaning}>{practice.meaning}</div>}
          <div className={styles.practiceActions}>
            <button className={styles.iconBtn} onClick={() => speakWord(practice.text)} title="Hear the phrase">
              <VolumeUpIcon fontSize="small" />
            </button>
            <button
              className={`${styles.iconBtn} ${practiceListening ? styles.iconBtnRecording : ''}`}
              onClick={togglePracticeMic}
              title="Say the phrase"
            >
              {practiceListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </button>
            <button className={styles.sendBtn} onClick={() => sendMessage(practice.text)} disabled={loadingChat}>
              Send anyway
            </button>
            <button className={styles.iconBtn} onClick={closePractice} title="Dismiss">
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {!practice && (
        <div className={styles.suggestRow}>
          {loadingSuggestions ? (
            <div className={styles.suggestLoading}>
              <span className={styles.typing}><span /><span /><span /></span>
              <span className={styles.suggestLoadingText}>thinking of replies…</span>
            </div>
          ) : (
            suggestions.map((s, i) => (
              <button
                key={`${i}-${s.text}`}
                className={styles.suggestCard}
                style={{ animationDelay: `${i * 0.09}s` }}
                onClick={() => selectSuggestion(s)}
              >
                <span className={styles.suggestText}>{s.text}</span>
                {romanizationOf(s.text, lang) && (
                  <span className={styles.suggestRoman}>{romanizationOf(s.text, lang)}</span>
                )}
                {s.meaning && <span className={styles.suggestMeaning}>{s.meaning}</span>}
              </button>
            ))
          )}
        </div>
      )}

      <div className={styles.inputRow}>
        <button
          className={`${styles.iconBtn} ${isRecording ? styles.iconBtnRecording : ''}`}
          onClick={toggleRecord}
          title="Dictate"
          disabled={loadingChat}
        >
          {isRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </button>
        <textarea
          ref={inputRef}
          className={styles.textarea}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              sendMessage(input)
            }
          }}
          placeholder={`Reply in ${langName}… try to use the target words`}
          autoComplete="off"
          spellCheck="false"
        />
        <button
          className={styles.iconBtn}
          onClick={() => fetchSuggestions(messages)}
          disabled={loadingSuggestions || loadingChat}
          title="Fresh reply suggestions"
        >
          <LightbulbIcon fontSize="small" />
        </button>
        <button className={styles.sendBtn} onClick={() => sendMessage(input)} disabled={!input.trim() || loadingChat}>
          Send
        </button>
      </div>

      <div className={styles.configRow}>
        <label className={styles.toggle}>
          <input type="checkbox" checked={autoSpeak} onChange={e => setAutoSpeak(e.target.checked)} />
          auto-speak
        </label>
        <label className={styles.toggle}>
          <input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} />
          romanization
        </label>
        <select className={styles.modelSelect} value={selectedModel} onChange={e => setSelectedModel(e.target.value)}>
          {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
    </div>
  )
}
